import { createElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { ACTION_CELL_CLASS, ACTION_TITLE_CLASS } from './content';
import { StyleMasterPriceRoot } from './styles';

export { buildStyleMasterPriceHtml } from './content';

/** 레거시 문서 표의 열 제목·버튼 */
const LEGACY_ACTION_TITLE_ID = 'del_reject_title';
const LEGACY_DELETE_CLASS = 'detail-hide';
const LEGACY_REJECT_CLASS = 'btn-detail-reject';

export type StyleMasterPriceTarget = { itemId: string; styleNo: string };

/** 부분 반려 버튼을 달 건 수 — V2 는 건(data-item-id) 단위, 레거시는 반려 버튼 수 */
export const countStyleMasterPriceItems = (content: string) =>
  new Set(Array.from(content.matchAll(/data-item-id="([^"]*)"/g), (m) => m[1]))
    .size + (content.match(/btn-detail-reject/g)?.length ?? 0);

/**
 * 저장 표 HTML 그대로(레거시 문서 포함). 마지막 열은 화면마다 다름 —
 * 작성은 삭제, 조회는 부분반려(차례인 결재자만 버튼), 편집은 제목만
 */
export const StyleMasterPriceSnapshot = ({
  content,
  actionTitle,
  onDelete,
  onPartialReject,
}: {
  content: string;
  actionTitle: string;
  onDelete?: (target: StyleMasterPriceTarget) => void;
  onPartialReject?: (target: StyleMasterPriceTarget) => void;
}) => {
  const { t } = useTranslation();

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const { classList } = element;
      const button = (target: StyleMasterPriceTarget) => {
        const handler = onDelete ?? onPartialReject;
        if (!handler || !target.itemId) return null;
        return (
          <BoxButton
            key="action"
            type="button"
            variant="secondary"
            size="small"
            onClick={() => handler(target)}
          >
            {onDelete ? t('삭제') : t('부분반려')}
          </BoxButton>
        );
      };
      if (
        classList.contains(ACTION_TITLE_CLASS) ||
        element.id === LEGACY_ACTION_TITLE_ID
      )
        return createElement(tagName, props, actionTitle);
      if (tagName === 'td' && classList.contains(ACTION_CELL_CLASS)) {
        const row = element.closest('tr');
        return createElement(
          'td',
          props,
          button({
            itemId: row?.getAttribute('data-item-id') ?? '',
            styleNo: row?.getAttribute('data-style-no') ?? '',
          }),
        );
      }
      if (classList.contains(LEGACY_DELETE_CLASS)) return null;
      if (classList.contains(LEGACY_REJECT_CLASS))
        return onPartialReject
          ? button({
              itemId: element.getAttribute('data-aprv_id') ?? '',
              styleNo: element.getAttribute('data-style_no') ?? '',
            })
          : null;
      return undefined;
    },
    [actionTitle, onDelete, onPartialReject, t],
  );

  return (
    <StyleMasterPriceRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </StyleMasterPriceRoot>
  );
};
